import React, {memo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {ToolbarButton} from '../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {FlexSpacer} from '../../../common/flex';
import {BaseComposerToolbar} from './baseComposerToolbar';
import {AnswersToolbarButtonProps} from './buttons/answersToolbarButton';
import {AnswersToolbarButtonContainer} from './buttons/answersToolbarButtonContainer';
import {AttachmentsToolbarButtonPublicProps} from './buttons/attachmentsToolbarButton';
import {AttachmentsToolbarButtonContainer} from './buttons/attachmentsToolbarButtonContainer';
import {DeleteToolbarButton, DeleteToolbarButtonProps} from './buttons/deleteToolbarButton';
import {DoneToolbarButton, DoneToolbarButtonProps} from './buttons/doneToolbarButton';
import {EmojiToolbarButton, EmojiToolbarButtonProps} from './buttons/emojiToolbarButton';
import {
  SendToolbarButtonContainer,
  SendToolbarButtonContainerProps,
} from './buttons/sendToolbarButtonContainer';
import {DraftSaveIndicator, DraftSaveStatusesEnum} from './draftSaveIndicator';

/*
 * Props.
 */

export interface ChatComposerToolbarProps
  extends EmojiToolbarButtonProps,
    AttachmentsToolbarButtonPublicProps,
    AnswersToolbarButtonProps,
    DeleteToolbarButtonProps,
    SendToolbarButtonContainerProps,
    DoneToolbarButtonProps {
  isUserVisitor?: boolean;
  canSendOnEnter: boolean;
  shouldSendOnEnter: boolean;
  updateShouldSendOnEnter: HandlerOf<boolean>;
  draftSaveStatus: DraftSaveStatusesEnum;
  isDraftSaving: boolean;
  isFormattingToolbarOpen?: boolean;
  toggleIsFormattingToolbarOpen?: Handler;
  hasSynchronousTimeline?: boolean;
}

/*
 * Style.
 */

const StyledEnterToSendDiv = styled.div`
  min-width: 0;
  margin: 0 10px;
  display: flex;
  align-items: center;
`;

/*
 * Component.
 */

export const ChatComposerToolbar = memo<ChatComposerToolbarProps>((props) => (
  <UserThemeConsumer>
    {(theme) => (
      <BaseComposerToolbar $hasSynchronousTimeline={props.hasSynchronousTimeline}>
        {props.hasSynchronousTimeline && (
          <ToolbarButton
            iconName="formatting"
            onClick={props.toggleIsFormattingToolbarOpen}
            label={<Trans id="composer-formatting">Formatting options</Trans>}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
            hasHover={props.isFormattingToolbarOpen}
          />
        )}
        <EmojiToolbarButton {...props} />
        <AttachmentsToolbarButtonContainer {...props} />
        {!props.isUserVisitor && <AnswersToolbarButtonContainer {...props} />}
        {!props.isUserVisitor && <DeleteToolbarButton {...props} />}
        <FlexSpacer />
        <DraftSaveIndicator status={props.draftSaveStatus} isSaving={props.isDraftSaving} />
        {renderRightActions(props)}
      </BaseComposerToolbar>
    )}
  </UserThemeConsumer>
));

function renderRightActions(props: ChatComposerToolbarProps) {
  if (props.isUserVisitor) {
    return <DoneToolbarButton {...props} />;
  }

  return (
    <>
      {maybeRenderEnterToSend(props)}
      <SendToolbarButtonContainer {...props} />
    </>
  );
}

function maybeRenderEnterToSend(props: ChatComposerToolbarProps) {
  if (!props.canSendOnEnter) {
    return null;
  }

  // Enter to send is the default in the new chat composer
  if (props.hasSynchronousTimeline) {
    return null;
  }

  return (
    <StyledEnterToSendDiv>
      <CheckBox isChecked={props.shouldSendOnEnter} onChange={props.updateShouldSendOnEnter}>
        <Trans id="composer-entertosend">&quot;Enter&quot; to send</Trans>
      </CheckBox>
    </StyledEnterToSendDiv>
  );
}
